/**
 * Music slot names, tidied for display.
 *
 * Slot names are free text typed by hand over several years, so the same slot
 * exists in the data under several spellings — "Closing Hymn", "Closing hymn",
 * "Closing", "Closing " with a trailing space. Grouped naively they become
 * separate sections, and because section headings are shown in capitals the
 * duplicates look identical: one heading with every hymn under it, and beside it
 * a heading that reads the same but holds a single stray hymn.
 *
 * The stored data was normalised on 2026-08-31 — trailing spaces stripped, and
 * "Closing"/"Closing hymn"/"Opening"/"Offertorty" rewritten to their canonical
 * spellings. This mapping is therefore no longer load-bearing for those cases;
 * it stays because the field is still free text and the next typo is a matter
 * of time. Genuinely distinct labels that only happen to share a slot, such as
 * "Entrance Procession" and "Gathering Song", were deliberately left alone in
 * the data and are folded here instead.
 *
 * Mirrors `backend/src/utils/slotNames.ts` — keep the two in step.
 */

const SLOT_ALIASES: Record<string, string> = {
  'opening': 'Opening Hymn',
  'opening hymn': 'Opening Hymn',
  'entrance': 'Opening Hymn',
  'entrance procession': 'Opening Hymn',
  'gathering song': 'Opening Hymn',
  'processional': 'Opening Hymn',
  'offertory': 'Offertory',
  'offertorty': 'Offertory',
  'offertory hymn': 'Offertory Hymn',
  'offertory anthem': 'Offertory Anthem',
  'communion': 'Communion',
  'closing': 'Closing Hymn',
  'closing hymn': 'Closing Hymn',
  'recessional': 'Closing Hymn',
};

const SLOT_ORDER: Record<string, number> = {
  'Prelude': 10,
  'Opening Hymn': 30,
  'Kyrie': 40,
  'Gloria': 50,
  'Psalm': 60,
  'Gospel': 70,
  'Creed': 80,
  'Asperges': 95,
  'Offertory Anthem': 100,
  'Offertory': 110,
  'Offertory Hymn': 115,
  'Sanctus': 120,
  "Lord's Prayer": 130,
  'Fraction': 140,
  'Communion': 150,
  'Closing Hymn': 160,
};

const UNRANKED = 900;

/** Fold spelling, spacing and casing variants of a slot name into one label. */
export const canonicalSlot = (placement?: string | null): string => {
  const cleaned = (placement || '').replace(/\s+/g, ' ').trim();
  if (!cleaned) return 'Other';
  return SLOT_ALIASES[cleaned.toLowerCase()] || cleaned;
};

/** Sort key placing a slot where it falls in the service; unknown slots last. */
export const slotRank = (label: string): number => SLOT_ORDER[label] ?? UNRANKED;
